import { createFileRoute } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect } from 'react';
import { z } from 'zod';

import { ActivityBoard } from '@apps/web/components/home/activity_board';
import {
    type ActivityDateRange,
    activityDateRangeFromSearch,
} from '@apps/web/components/home/activity_filter_state';
import {
    type ActivityPresentation,
    ActivityPresentationToggle,
} from '@apps/web/components/home/activity_presentation_toggle';
import { Button } from '@apps/web/components/ui/button';
import { ListPageHeader } from '@apps/web/components/ui/list_page_header';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { ActivityFeedRoute } from '@apps/web/routes/_app/(main)/activity_feed_route';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

export const activitySearchSchema = z.object({
    eventId: z.string().optional(),
    notificationId: z.string().optional(),
    taskId: z.string().optional(),
    itemOrganizationId: z.string().optional(),
    inboxView: z
        .enum([
            'inbox',
            'unread',
            'read',
            'needs_you',
            'assigned_to_me',
            'unassigned',
            'done',
            'all_history',
            'dismissed',
        ])
        .optional(),
    notificationType: z.string().optional(),
    inboxSection: z.enum(['all', 'attention', 'notifications']).optional(),
    assignee: z.string().optional(),
    status: z
        .enum(['open', 'done', 'needs-attention', 'all-history'])
        .optional(),
    view: z.enum(['needs_attention', 'all']).optional(),
    filter: z.string().optional(),
    org: z.string().optional(),
    office: z.string().optional(),
    coordinator: z.string().optional(),
    tag: z.string().optional(),
    excludedTag: z.string().optional(),
    q: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
    presentation: z.enum(['feed', 'list', 'board']).optional(),
});

// The last List/Board choice, so returning to Activity reopens the same view.
// It is a per-browser display preference, like the feed's other view settings.
const PRESENTATION_STORAGE_KEY = 'phoebe:activityPresentation';

function readStoredPresentation(): ActivityPresentation | null {
    try {
        const stored = window.localStorage.getItem(PRESENTATION_STORAGE_KEY);
        return stored === 'board' || stored === 'list' ? stored : null;
    } catch {
        // localStorage may be unavailable in private browsing.
        return null;
    }
}

export const Route = createFileRoute('/_app/(main)/activity')({
    component: ActivityRoute,
    validateSearch: activitySearchSchema,
    head: ({ match }) => ({
        meta: [
            {
                title:
                    match.search.inboxSection === 'all'
                        ? 'Inbox | Phoebe'
                        : 'Activity | Phoebe',
            },
        ],
    }),
});

function ActivityRoute() {
    const organizationId = useOrganizationId();
    const posthog = usePostHog();
    const navigate = Route.useNavigate();
    const {
        presentation: presentationParam,
        from: fromParam,
        to: toParam,
        inboxSection,
        eventId,
        notificationId,
        taskId,
    } = Route.useSearch();
    const { data: organization, isLoading: isLoadingOrganization } =
        useOrganization(organizationId, {
            reason: 'activity-route',
        });
    const unifiedInboxEnabled = useFeatureFlag(
        'unified_activity_inbox_enabled',
        organization?.features,
    );
    const isRedesignEnabled = useFeatureFlag(
        'activity_feed_redesign_enabled',
        organization?.features,
    );
    const requestedPresentation: ActivityPresentation =
        presentationParam === 'board' ? 'board' : 'list';
    // The board shares the list view's `from`/`to` search params, so a
    // chosen date range survives toggling between presentations.
    const boardDateRange = activityDateRangeFromSearch(fromParam, toParam);
    const handleBoardDateRangeChange = useCallback(
        (nextRange: ActivityDateRange | null) => {
            void navigate({
                to: '/activity',
                search: (previous) => ({
                    ...previous,
                    from: nextRange?.from,
                    to: nextRange?.to,
                }),
                replace: true,
            });
        },
        [navigate],
    );
    const handlePresentationChange = useCallback(
        (nextPresentation: ActivityPresentation) => {
            try {
                window.localStorage.setItem(
                    PRESENTATION_STORAGE_KEY,
                    nextPresentation,
                );
            } catch {
                // Losing persistence is acceptable; the toggle still applies.
            }
            if (nextPresentation !== requestedPresentation) {
                try {
                    posthog?.capture(
                        EVENTS.ACTIVITY_BOARD_PRESENTATION_SWITCHED,
                        {
                            organization_id: organizationId,
                            from_presentation: requestedPresentation,
                            to_presentation: nextPresentation,
                        },
                    );
                } catch {
                    // Analytics must never block the presentation toggle.
                }
            }
            void navigate({
                to: '/activity',
                search: (previous) => ({
                    ...previous,
                    presentation:
                        nextPresentation === 'board' ? 'board' : undefined,
                    ...(nextPresentation === 'board'
                        ? {
                              eventId: undefined,
                              notificationId: undefined,
                              taskId: undefined,
                              itemOrganizationId: undefined,
                          }
                        : {}),
                }),
                replace: true,
            });
        },
        [navigate, organizationId, posthog, requestedPresentation],
    );

    // Canonicalize the presentation param once the flag state is known:
    // `board` is the only meaningful value, so legacy `feed`/`list` values
    // (and `board` itself when the redesign is off) get stripped. Wait for
    // the organization to actually load before acting — before the
    // organization id resolves, the (disabled) query reports
    // isLoading=false while the flag still reads false, and stripping then
    // would drop a deep-linked ?presentation=board on every fresh load.
    //
    // A bare /activity visit reopens the board when it was the last view
    // chosen. Links to a specific item or the Inbox keep their own view.
    const shouldRestoreBoard =
        organization !== undefined &&
        isRedesignEnabled &&
        presentationParam === undefined &&
        inboxSection !== 'all' &&
        eventId === undefined &&
        notificationId === undefined &&
        taskId === undefined &&
        readStoredPresentation() === 'board';
    useEffect(() => {
        if (organization === undefined) {
            return;
        }
        if (shouldRestoreBoard) {
            void navigate({
                to: '/activity',
                search: (previous) => ({
                    ...previous,
                    presentation: 'board',
                }),
                replace: true,
            });
            return;
        }
        const shouldStrip = isRedesignEnabled
            ? presentationParam === 'feed' || presentationParam === 'list'
            : presentationParam === 'board';
        if (!shouldStrip) {
            return;
        }
        // A link that names the list is a choice too; record it so the bare
        // URL left after stripping does not restore the board.
        if (presentationParam === 'list') {
            try {
                window.localStorage.setItem(PRESENTATION_STORAGE_KEY, 'list');
            } catch {
                // Losing persistence is acceptable; the link still applies.
            }
        }
        void navigate({
            to: '/activity',
            search: (previous) => ({
                ...previous,
                presentation: undefined,
            }),
            replace: true,
        });
    }, [
        isRedesignEnabled,
        navigate,
        organization,
        presentationParam,
        shouldRestoreBoard,
    ]);

    if (!organizationId) {
        return null;
    }
    // Render nothing until the restore redirect lands, so the list does not
    // flash (and fetch) before the board.
    if (isLoadingOrganization || shouldRestoreBoard) {
        return null;
    }

    if (
        inboxSection === 'all' ||
        (unifiedInboxEnabled &&
            (requestedPresentation === 'list' || !isRedesignEnabled))
    ) {
        return (
            <ActivityFeedRoute
                organizationId={organizationId}
                unifiedInboxEnabled={true}
                phoebeHelpEnabled={isRedesignEnabled}
                onOpenActivityBoard={
                    isRedesignEnabled && inboxSection !== 'all'
                        ? () => handlePresentationChange('board')
                        : undefined
                }
            />
        );
    }

    if (!isRedesignEnabled) {
        return <ActivityFeedRoute organizationId={organizationId} />;
    }

    const presentationToggle = (
        <ActivityPresentationToggle
            value={requestedPresentation}
            onChange={handlePresentationChange}
        />
    );

    if (requestedPresentation === 'list') {
        return (
            <ActivityFeedRoute
                organizationId={organizationId}
                titleAction={presentationToggle}
                phoebeHelpEnabled={true}
            />
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-2 p-2 md:p-4">
            {unifiedInboxEnabled ? (
                <Button
                    variant="ghost"
                    className="w-fit"
                    onClick={() => handlePresentationChange('list')}
                >
                    Back to Inbox
                </Button>
            ) : (
                <ListPageHeader
                    title="Activity"
                    actions={presentationToggle}
                    hideTitleOnPhone={true}
                />
            )}
            <div className="min-h-0 flex-1">
                <ActivityBoard
                    organizationId={organizationId}
                    layout="board"
                    dateRange={boardDateRange}
                    onDateRangeChange={handleBoardDateRangeChange}
                />
            </div>
        </div>
    );
}
